/**
 * Default Agent Profile Service - React Query Integration
 * Name + avatar of the free agent created for every newly registered user
 * (/v1/admin/default-agent-profile).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

export interface DefaultAgentProfile {
  name: string;
  avatarPublicId: string | null;
  avatar: {
    publicId: string;
    name: string;
    type: string;
    previewUrl: string | null;
    isActive: boolean;
  } | null;
}

export interface UpdateDefaultAgentProfilePayload {
  name: string;
  avatarPublicId: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

const BASE = '/v1/admin/default-agent-profile';

export const defaultAgentProfileKeys = {
  detail: () => ['admin', 'default-agent-profile'] as const,
};

const fetchDefaultAgentProfile = async (): Promise<DefaultAgentProfile> => {
  const response = await axiosInstance.get<ApiResponse<DefaultAgentProfile>>(BASE);
  if (!response.data.data) {
    throw new Error('Default agent profile response is empty');
  }
  return response.data.data;
};

const updateDefaultAgentProfile = async (
  payload: UpdateDefaultAgentProfilePayload
): Promise<DefaultAgentProfile> => {
  const response = await axiosInstance.put<ApiResponse<DefaultAgentProfile>>(BASE, payload);
  if (!response.data.data) {
    throw new Error(response.data.message || 'Default agent profile response is empty');
  }
  return response.data.data;
};

export const useDefaultAgentProfile = () =>
  useQuery({
    queryKey: defaultAgentProfileKeys.detail(),
    queryFn: fetchDefaultAgentProfile,
    staleTime: 60 * 1000,
  });

export const useUpdateDefaultAgentProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDefaultAgentProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(defaultAgentProfileKeys.detail(), data);
    },
  });
};
